import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Feature } from "geojson";
import { enqueueSnackbar } from "notistack";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { mergeLayerAttributes } from "fond/draw/attributeHelpers";
import FeatureAttributesForm from "fond/draw/FeatureAttributesForm";
import { HistoryContext, useHistory } from "fond/history";
import { MapContext } from "fond/map/MapProvider";
import { useAppSelector } from "fond/utils/hooks";

const PropertiesPanelEdit: React.FC = () => {
  const { map, drawControl } = useContext(MapContext);
  const { set: setHistory } = useHistory(HistoryContext);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versionConfig = useAppSelector((state) => selectVersionConfig(state, versionId));

  // Get editingLayerId from drawControl options
  const editingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;

  // Get the layer configuration with attributes
  const layerConfig = useAppSelector((state) => {
    if (!editingLayerId) return undefined;

    // First, try to get it as a layer
    const layer = selectLayerByVersionAndLayerKey(state, { versionId, layerId: editingLayerId });
    if (layer) return layer;

    // If not found, check if it's a sublayer and get the parent layer
    const config = versionConfig?.Data?.entities?.[editingLayerId];
    if (config?.Type === "SUBLAYER" && config.ParentID) {
      // Get the parent layer using the ParentID
      const parentConfig = versionConfig?.Data?.entities?.[config.ParentID];
      if (parentConfig?.Type === "LAYER") {
        return selectLayerByVersionAndLayerKey(state, { versionId, layerId: parentConfig.Key });
      }
    }

    return undefined;
  });

  // Get the current EditingLayer configuration
  // If editingLayerId is a sublayer, we need to find the parent layer in allLayers
  const editingLayer = React.useMemo(() => {
    if (!editingLayerId) return undefined;

    // First, try to find it directly by ID
    const layer = drawControl.current?.options?.editOptions?.allLayers?.find((l) => l.ID === editingLayerId);
    if (layer) return layer;

    // If not found, check if it's a sublayer and get the parent layer
    const config = versionConfig?.Data?.entities?.[editingLayerId];
    if (config?.Type === "SUBLAYER" && config.ParentID) {
      // Get the parent layer configuration
      const parentConfig = versionConfig?.Data?.entities?.[config.ParentID];
      if (parentConfig?.Type === "LAYER") {
        // Find the parent layer in allLayers using the parent's Key
        return drawControl.current?.options?.editOptions?.allLayers?.find((l) => l.ID === parentConfig.Key);
      }
    }

    return undefined;
  }, [editingLayerId, versionConfig, drawControl]);

  const handleOnSelect = useCallback(() => {
    if (drawControl.current?.getSelectedIds().length === 1) {
      const feature = drawControl.current?.getSelected().features[0];
      setSelectedFeature(feature || null);
    } else {
      setSelectedFeature(null);
    }
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleSubmit = useCallback(
    (_values: Record<string, string | number | boolean>) => {
      // Update history silently after attribute values are set
      if (drawControl.current?.getAll()) {
        setHistory(drawControl.current.getAll(), { silent: true });
      }

      // Refresh the selected feature after save
      if (selectedFeature?.id && drawControl.current) {
        const updatedFeature = drawControl.current.get(selectedFeature.id as string);
        if (updatedFeature) {
          setSelectedFeature(updatedFeature as Feature);
        }
      }
      enqueueSnackbar("Feature attributes updated", { variant: "success" });
    },
    [selectedFeature, drawControl, setHistory]
  );

  if (!selectedFeature) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="textSecondary">
          Select a feature to edit properties
        </Typography>
      </Box>
    );
  }

  // Merge attributes from EditingLayer with layer schema
  const attributes = mergeLayerAttributes(layerConfig?.Attributes, editingLayer);

  if (attributes.length === 0) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="textSecondary">
          This layer has no editable attributes
        </Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box p={2} pb={1}>
        <Typography variant="subtitle2" gutterBottom>
          {layerConfig?.Configuration.Label || editingLayerId}
        </Typography>
      </Box>
      <Box flex={1} sx={{ minHeight: 0 }}>
        <FeatureAttributesForm feature={selectedFeature} attributes={attributes} onSubmit={handleSubmit} showReset disableOnPristine />
      </Box>
    </Box>
  );
};

export default PropertiesPanelEdit;
